import React from 'react';

/**
 * BiologicalCeasefireBanner — § III of the Home manifesto.
 *
 * Full-bleed near-black panel that interrupts the scroll like a
 * sovereign placard. One sentence, broken across two lines.
 *
 * The dark contrast is intentional: it's the only black section of
 * the home page and acts as a typographic "still point" between the
 * Steel-Blue Trinity (§ II) and the Powder-Blue Harmony (§ IV).
 */

const BiologicalCeasefireBanner = ({ testId = 'home-ceasefire' }) => (
  <section
    data-testid={testId}
    aria-label="The Biological Ceasefire"
    className="relative w-full overflow-hidden"
    style={{
      background:
        'radial-gradient(ellipse at 50% 35%, #1A1A1F 0%, #0E0E12 65%, #050507 100%)',
    }}
  >
    {/* Grain veil — almost imperceptible */}
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 mix-blend-overlay opacity-30"
      style={{
        backgroundImage:
          'radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)',
        backgroundSize: '3px 3px',
      }}
    />
    {/* Gold halo behind the headline */}
    <div
      aria-hidden
      className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[55%] opacity-[0.13] blur-3xl"
      style={{
        background:
          'radial-gradient(ellipse at center, rgba(212,175,90,0.7) 0%, transparent 70%)',
      }}
    />

    <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 py-28 md:py-40 min-h-[78vh] flex flex-col items-center justify-center text-center">
      <p
        data-testid={`${testId}-overline`}
        className="font-mono text-[10px] md:text-[12px] tracking-[0.46em] uppercase font-semibold text-[#D4AF5A] mb-10 md:mb-14 flex items-center gap-4"
      >
        <span aria-hidden className="inline-block w-10 h-px bg-[#D4AF5A]" />
        The Biological Ceasefire
        <span aria-hidden className="inline-block w-10 h-px bg-[#D4AF5A]" />
      </p>

      <h2
        data-testid={`${testId}-statement`}
        className="font-serif font-light text-[#F4F8F9] leading-[0.92] tracking-[-0.015em]"
        style={{ fontSize: 'clamp(40px, 7.5vw, 132px)' }}
      >
        We do not alter
        <br />
        your nature.
        <br />
        <span className="text-[#D4AF5A]">We <em>armor</em> it.</span>
      </h2>

      <p
        data-testid={`${testId}-coda`}
        className="mt-12 md:mt-16 font-mono text-[10px] md:text-[11px] tracking-[0.38em] uppercase text-[#F4F8F9]/55"
      >
        Recorded · not recited
      </p>
    </div>

    {/* Gold rule top + bottom */}
    <span
      aria-hidden
      className="absolute top-0 left-0 right-0 h-px"
      style={{
        background:
          'linear-gradient(90deg, transparent, rgba(212,175,90,0.55) 40%, rgba(212,175,90,0.55) 60%, transparent)',
      }}
    />
    <span
      aria-hidden
      className="absolute bottom-0 left-0 right-0 h-px"
      style={{
        background:
          'linear-gradient(90deg, transparent, rgba(212,175,90,0.55) 40%, rgba(212,175,90,0.55) 60%, transparent)',
      }}
    />
  </section>
);

export default BiologicalCeasefireBanner;
